/**
 * Currency formatting utilities for Prosci Portal subscriptions
 * Supports multiple currencies used across Stripe regional accounts
 */

export type CurrencyCode = 'USD' | 'CAD' | 'GBP' | 'EUR' | 'AUD' | 'BRL' | 'NZD' | 'SGD';

interface CurrencyConfig {
  symbol: string;
  code: string;
  locale: string;
  position: 'before' | 'after';
}

const CURRENCY_CONFIGS: Record<CurrencyCode, CurrencyConfig> = {
  USD: {
    symbol: '$',
    code: 'USD',
    locale: 'en-US',
    position: 'before'
  },
  CAD: {
    symbol: 'CA$',
    code: 'CAD',
    locale: 'en-CA',
    position: 'before'
  },
  GBP: {
    symbol: '£',
    code: 'GBP',
    locale: 'en-GB',
    position: 'before'
  },
  EUR: {
    symbol: '€',
    code: 'EUR',
    locale: 'en-IE',
    position: 'before'
  },
  AUD: {
    symbol: 'A$',
    code: 'AUD',
    locale: 'en-AU',
    position: 'before'
  },
  BRL: {
    symbol: 'R$',
    code: 'BRL',
    locale: 'pt-BR',
    position: 'before'
  },
  NZD: {
    symbol: 'NZ$',
    code: 'NZD',
    locale: 'en-NZ',
    position: 'before'
  },
  SGD: {
    symbol: 'S$',
    code: 'SGD',
    locale: 'en-SG',
    position: 'before'
  }
};

/**
 * Format an amount in cents to a display string with currency symbol
 * @param amountInCents - The price amount in cents (smallest currency unit)
 * @param currencyCode - ISO 4217 currency code
 * @param showCode - Whether to show the currency code (e.g., "USD") alongside symbol
 * @returns Formatted price string (e.g., "$99.00", "€85.00")
 */
export function formatPrice(
  amountInCents: number,
  currencyCode: CurrencyCode = 'USD',
  showCode = false
): string {
  const config = CURRENCY_CONFIGS[currencyCode] || CURRENCY_CONFIGS.USD;

  // Convert cents to main currency unit
  const amount = amountInCents / 100;

  // Format with appropriate locale
  const formatter = new Intl.NumberFormat(config.locale, {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const formattedAmount = formatter.format(amount);

  // Construct final string with symbol position
  const priceWithSymbol = config.position === 'before'
    ? `${config.symbol}${formattedAmount}`
    : `${formattedAmount} ${config.symbol}`;

  return showCode ? `${priceWithSymbol} ${config.code}` : priceWithSymbol;
}

/**
 * Format monthly subscription price
 * @param amountInCents - The price amount in cents
 * @param currencyCode - ISO 4217 currency code
 * @returns Formatted monthly price (e.g., "$99.00/mo")
 */
export function formatMonthlyPrice(
  amountInCents: number,
  currencyCode: CurrencyCode = 'USD'
): string {
  return `${formatPrice(amountInCents, currencyCode)}/mo`;
}

/**
 * Structured annual price data for rendering with separate styling
 */
export interface AnnualPriceData {
  /** Main price portion (e.g., "$149/yr") */
  price: string;
  /** Currency suffix for non-USD currencies (e.g., "CAD"), null for USD */
  currencySuffix: string | null;
}

/**
 * Format annual subscription price (compact version for card display)
 * Uses base symbol ($, £, €) with currency code suffix for non-USD currencies
 * @param amountInCents - The price amount in cents
 * @param currencyCode - ISO 4217 currency code
 * @returns Formatted annual price without decimals (e.g., "$149/yr", "$149/yr CAD")
 */
export function formatAnnualPrice(
  amountInCents: number,
  currencyCode: CurrencyCode = 'USD'
): string {
  const data = getAnnualPriceData(amountInCents, currencyCode);
  return data.currencySuffix ? `${data.price} ${data.currencySuffix}` : data.price;
}

/**
 * Get structured annual price data for custom rendering
 * Allows separate styling of price and currency suffix
 * @param amountInCents - The price amount in cents
 * @param currencyCode - ISO 4217 currency code
 * @returns Object with price and optional currency suffix
 */
export function getAnnualPriceData(
  amountInCents: number,
  currencyCode: CurrencyCode = 'USD'
): AnnualPriceData {
  const config = CURRENCY_CONFIGS[currencyCode] || CURRENCY_CONFIGS.USD;

  // Convert cents to main currency unit
  const amount = amountInCents / 100;

  // Format without decimals for annual pricing
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  const formattedAmount = formatter.format(amount);

  // Use compact base symbols for dollar-based currencies
  const compactSymbols: Record<string, string> = {
    USD: '$',
    CAD: '$',
    AUD: '$',
    NZD: '$',
    SGD: '$',
    GBP: '£',
    EUR: '€',
    BRL: 'R$'
  };

  const symbol = compactSymbols[currencyCode] || config.symbol;
  const price = `${symbol}${formattedAmount}/yr`;

  // For USD, no suffix needed. For others, return currency code as suffix
  return {
    price,
    currencySuffix: currencyCode === 'USD' ? null : currencyCode
  };
}

/**
 * Get currency symbol for a given currency code
 * @param currencyCode - ISO 4217 currency code
 * @returns Currency symbol (e.g., "$", "€", "£")
 */
export function getCurrencySymbol(currencyCode: CurrencyCode = 'USD'): string {
  return CURRENCY_CONFIGS[currencyCode]?.symbol || '$';
}

/**
 * Check if currency code is valid
 * @param code - Currency code to validate
 * @returns true if valid currency code
 */
export function isValidCurrency(code: string): code is CurrencyCode {
  return code in CURRENCY_CONFIGS;
}

/**
 * Get all supported currency codes
 * @returns Array of supported currency codes
 */
export function getSupportedCurrencies(): CurrencyCode[] {
  return Object.keys(CURRENCY_CONFIGS) as CurrencyCode[];
}
